'use client';

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSession } from 'next-auth/react';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { useOrganization } from '@/contexts/OrganizationContext';
import { buildOrgPath } from '@/lib/org-url';
import { refreshPortfolioSummary } from '@/lib/refresh-kpis';
import { BELL_REFRESH_EVENT } from '@/lib/bell-refresh';
import { TASK_DERIVED_SIGNAL_CATEGORIES } from '@/lib/signal-category-visuals';
import { actionQueueQueryKey } from '@/hooks/useActionQueue';
import type { Signal, SignalBusinessContext, SignalEvidence, SignalPortfolioRollup, SignalCategory, SignalFeedResponse, SignalFilterOptions, UnreadCountResponse, SignalActionResponse, DismissReason } from '@/types/signals';
import type { CreateActionInput } from '@/types/actions';

// ==================== Context Type ====================

interface SignalFeedContextValue {
  /** Filtered signal list used by the Signals Log tab. */
  signals: Signal[];
  /** Always-fresh, unfiltered signals for the home-page panel (top 10). */
  panelSignals: Signal[];
  /** Panel-preview counterpart of panelSignals for the "Acted On" tab —
   * top 10 already-resolved signals, kept loaded even while the panel is
   * collapsed so switching tabs there doesn't need to expand first. */
  panelActedOnSignals: Signal[];
  /** Total matching count for the panel's "Active" preview (unfiltered, all-time). */
  panelTotalCount: number;
  /** Total matching count for the panel's "Acted On" preview. */
  panelActedOnTotalCount: number;
  totalCount: number;
  /** GitHub #683: signals hidden from the most recent fetchSignals/refreshSignals
   * call solely by the caller's in_app_muted_categories preference. 0 once
   * showMutedSignals() has been called for the current filter set. */
  mutedCount: number;
  /** GitHub #683: re-fetch the current Signals Log filter set bypassing mutes
   * (session-local — the next normal refresh goes back to respecting them). */
  showMutedSignals: () => Promise<void>;
  unreadCount: number;
  byPriority: UnreadCountResponse['byPriority'];
  featureEnabled: boolean;
  loading: boolean;
  loadingMore: boolean;
  /** Set when the last fetchSignals call failed (transient backend error). */
  fetchError: string | null;
  nextCursor: string | null;
  /** Mark a single signal as read (optimistic). */
  markRead: (signalId: string) => Promise<void>;
  /** Mark all signals as read (optimistic). */
  markAllRead: () => Promise<void>;
  /** GitHub #619: materialize a signal's recommended action into the Action
   * Queue (account_actions). Returns the new action_id. */
  addRecommendedActionToQueue: (signalId: string, overrides?: Partial<CreateActionInput>) => Promise<string>;
  /** GitHub #619: mark a signal's recommended action as "no action needed".
   * `reason` (GitHub #1051 Signals-page review, Decided #2) records why —
   * "Dismissed — not relevant / data error" — for later severity/noise tuning. */
  dismissRecommendedAction: (signalId: string, reason?: DismissReason) => Promise<void>;
  /** Move a signal from Active to Acted On locally, without patching
   * recommendedAction — for the manual "Add to:" targets (see
   * SignalDetailModal.tsx's handleAddTo). */
  markSignalActedOn: (signalId: string) => void;
  /** Refresh the signal list from scratch (used by Signals Log with filters). */
  refreshSignals: (filters?: SignalFilterOptions) => Promise<void>;
  /** Load the next page of signals (cursor pagination). */
  loadMore: () => Promise<void>;
  /** Admin/superadmin account-scope for the home-page panel's Active/Acted On
   * previews (Command Hub's My Customers / All Customers toggle) — set by
   * SignalFeedPanel so panel fetches (initial load, urgent-signal poll) stay
   * in the currently selected scope. */
  setPanelViewAll: (viewAll: boolean) => void;
}

const defaultByPriority = { urgent: 0, high: 0, medium: 0, low: 0 };

const SignalFeedContext = createContext<SignalFeedContextValue>({
  signals: [],
  panelSignals: [],
  panelActedOnSignals: [],
  panelTotalCount: 0,
  panelActedOnTotalCount: 0,
  totalCount: 0,
  mutedCount: 0,
  showMutedSignals: async () => {},
  unreadCount: 0,
  byPriority: defaultByPriority,
  featureEnabled: false,
  loading: true,
  loadingMore: false,
  fetchError: null,
  nextCursor: null,
  markRead: async () => {},
  markAllRead: async () => {},
  addRecommendedActionToQueue: async () => '',
  dismissRecommendedAction: async () => {},
  markSignalActedOn: () => {},
  refreshSignals: async () => {},
  loadMore: async () => {},
  setPanelViewAll: () => {},
});

// ==================== Helpers ====================

const API_BASE = '/api/ai-services';

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...init?.headers },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const status = body?.featureEnabled === false ? 503 : res.status;
    const err = Object.assign(new Error(body?.error || body?.detail || `HTTP ${res.status}`), { status, _debug: body?._debug });
    throw err;
  }
  const body = await res.json();
  // Backend returns 200 with featureEnabled:false when signals aren't available
  if (body?.featureEnabled === false) {
    const err = Object.assign(new Error('Feature disabled'), { status: 503, _debug: body?._debug });
    throw err;
  }
  return body;
}

// ==================== Provider ====================

const URGENT_POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes
// Lower-frequency refresh of the general (non-urgent) filtered list, so
// daily/hourly-cadence categories (e.g. activity, product changes) show up
// without the user needing to change filters or manually refresh.
const LIST_POLL_INTERVAL = 10 * 60 * 1000; // 10 minutes

export function SignalFeedProvider({ children }: { children: React.ReactNode }) {
  const { data: session, status: authStatus } = useSession();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { orgId: urlOrgId } = useOrganization();
  const isAuthenticated = authStatus === 'authenticated' && !!session?.user;
  const activeOrgId = session?.user?.activeOrganizationId ?? null;
  // This provider is mounted app-wide, including org-agnostic pages with no
  // [orgId] in the URL — prefer the URL's org id, fall back to the session's
  // active org (still correct, just not reflected in the current URL).
  const navigateToAccount = (path: string) => {
    const orgId = urlOrgId ?? session?.user?.activeOrganizationId;
    router.push(orgId ? buildOrgPath(orgId, path) : `/${path}`);
  };

  // State
  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [signals, setSignals] = useState<Signal[]>([]);
  const [fetchError, setFetchError] = useState<string | null>(null);
  /** Independent state for the home-page panel – never overwritten by Signals Log filters. */
  const [panelSignals, setPanelSignals] = useState<Signal[]>([]);
  const [panelActedOnSignals, setPanelActedOnSignals] = useState<Signal[]>([]);
  const [panelTotalCount, setPanelTotalCount] = useState(0);
  const [panelActedOnTotalCount, setPanelActedOnTotalCount] = useState(0);
  // Account scope for the panel previews — set by SignalFeedPanel from the
  // Command Hub's My Customers / All Customers toggle.
  const [panelViewAll, setPanelViewAll] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  /** Signals hidden by the caller's in_app_muted_categories preference on the
   * most recent fetch (GitHub #683) — backs the "N signals muted — show"
   * affordance in the Signals Log. */
  const [mutedCount, setMutedCount] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [byPriority, setByPriority] = useState<UnreadCountResponse['byPriority']>(defaultByPriority);

  // Refs for polling
  const lastUrgentPollRef = useRef<string>(new Date().toISOString());
  const urgentTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const listTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeFiltersRef = useRef<SignalFilterOptions>({});

  // ---- Reset on org switch ----
  // Switching the active organization (ProfileButton.tsx's
  // handleSwitchOrganization) is a client-side navigation that never
  // unmounts this provider, so without this, the previous org's signals/
  // counts would keep rendering on the new org's Command Hub until the
  // next poll happened to overwrite them. Skip the very first render (that's
  // just the initial mount, not a switch).
  const isFirstOrgRender = useRef(true);
  useEffect(() => {
    if (isFirstOrgRender.current) {
      isFirstOrgRender.current = false;
      return;
    }
    setSignals([]);
    setPanelSignals([]);
    setPanelActedOnSignals([]);
    setPanelTotalCount(0);
    setPanelActedOnTotalCount(0);
    setTotalCount(0);
    setMutedCount(0);
    setNextCursor(null);
    setUnreadCount(0);
    setByPriority(defaultByPriority);
  }, [activeOrgId]);

  // ---- Feature-flag probe ----
  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        console.log('[SignalFeed] Probing unread-count endpoint...');
        const res = await apiFetch<UnreadCountResponse>('/api/v1/signals/unread-count');
        if (cancelled) return;
        console.log('[SignalFeed] Probe SUCCESS — unreadCount:', res.unreadCount, 'byPriority:', res.byPriority);
        setFeatureEnabled(true);
        setUnreadCount(res.unreadCount ?? 0);
        setByPriority(res.byPriority ?? defaultByPriority);
      } catch (err: any) {
        if (cancelled) return;
        if (err.status === 503) {
          // Genuine "feature disabled for this org" — hide the feature, as before.
          console.warn('[SignalFeed] Probe returned 503 (feature disabled). _debug:', (err as any)._debug);
          setFeatureEnabled(false);
        } else {
          // Transient error (network, 5xx, etc.) — don't conflate this with
          // the org lacking the feature. Assume enabled so tabs/panels render
          // and their own fetches surface the existing "Failed to load
          // signals" retry UI instead of the feature silently vanishing.
          console.error('[SignalFeed] Probe FAILED with transient error (assuming enabled):', err.message, '| status:', err.status, '| _debug:', (err as any)._debug);
          setFeatureEnabled(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // activeOrgId re-runs the probe on org switch — the feature flag and
    // unread count are both org-scoped and would otherwise keep showing the
    // previous org's values (see the reset effect above).
  }, [isAuthenticated, activeOrgId]);

  // ---- Fetch signals ----
  // `silent`: used by the background list poll — updates data without toggling
  // loading/error state, so it doesn't flash the skeleton loader or error card
  // over an already-rendered list.
  const fetchSignals = useCallback(async (filters: SignalFilterOptions = {}, append = false, silent = false) => {
    if (!featureEnabled) return;
    if (!silent) {
      if (!append) setLoading(true);
      else setLoadingMore(true);
      setFetchError(null);
    }

    try {
      const url = buildSignalsUrl(filters);
      console.log('[SignalFeed] fetchSignals →', url);
      const res = await apiFetch<SignalFeedResponse>(url);
      console.log('[SignalFeed] fetchSignals response: totalCount=', res.totalCount, 'signals=', res.signals?.length, '_debug=', (res as any)._debug);
      if (append) {
        setSignals(prev => [...prev, ...normalizeSignals(res.signals)]);
      } else {
        setSignals(normalizeSignals(res.signals));
      }
      setNextCursor(res.nextCursor ?? null);
      setTotalCount(res.totalCount ?? 0);
      setMutedCount(filters.respectMutes === false ? 0 : (res.mutedCount ?? 0));
    } catch (err: any) {
      const msg = err.message || 'Failed to load signals';
      console.error('[SignalFeed] fetchSignals FAILED:', msg, '| status:', err.status, '| _debug:', (err as any)._debug);
      if (!silent) {
        setFetchError(msg);
      }
      // Don't clear existing signals on error so previous results remain visible
    } finally {
      if (!silent) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [featureEnabled]);

  // ---- Fetch panel signals (home-page panel, top 10, unwindowed), scoped by
  // panelViewAll — refetches automatically whenever that scope changes since
  // it's a dependency of this callback (see the initial-load effect below). ----
  const fetchPanelSignals = useCallback(async () => {
    if (!featureEnabled) return;
    try {
      const res = await apiFetch<SignalFeedResponse>(buildSignalsUrl({ limit: 10, viewAll: panelViewAll }));
      setPanelSignals(normalizeSignals(res.signals));
      setPanelTotalCount(res.totalCount ?? 0);
    } catch (err: any) {
      console.warn('[SignalFeed] fetchPanelSignals failed:', err.message);
    }
  }, [featureEnabled, panelViewAll]);

  // ---- Fetch panel "Acted On" signals — same top-10 preview, resolved set.
  // Kept loaded alongside panelSignals so the collapsed SignalFeedPanel can
  // switch tabs without expanding first.
  const fetchPanelActedOnSignals = useCallback(async () => {
    if (!featureEnabled) return;
    try {
      const res = await apiFetch<SignalFeedResponse>(
        buildSignalsUrl({ limit: 10, actedOn: true, viewAll: panelViewAll })
      );
      setPanelActedOnSignals(normalizeSignals(res.signals));
      setPanelActedOnTotalCount(res.totalCount ?? 0);
    } catch (err: any) {
      console.warn('[SignalFeed] fetchPanelActedOnSignals failed:', err.message);
    }
  }, [featureEnabled, panelViewAll]);

  // Initial signal load once feature flag is confirmed — also re-runs on
  // activeOrgId so a switch reloads even when featureEnabled was already
  // true for the previous org (a same-value setState from the probe effect
  // above wouldn't otherwise re-trigger this effect).
  useEffect(() => {
    if (featureEnabled) {
      fetchSignals({ limit: 50 });
      fetchPanelSignals();
      fetchPanelActedOnSignals();
    }
  }, [featureEnabled, fetchSignals, fetchPanelSignals, fetchPanelActedOnSignals, activeOrgId]);

  // ---- Public actions ----

  const refreshSignals = useCallback(async (filters?: SignalFilterOptions) => {
    activeFiltersRef.current = filters ?? {};
    await fetchSignals({ limit: 50, ...filters });
    // Also refresh unread count
    try {
      const res = await apiFetch<UnreadCountResponse>('/api/v1/signals/unread-count');
      setUnreadCount(res.unreadCount ?? 0);
      setByPriority(res.byPriority ?? defaultByPriority);
    } catch { /* ignore */ }
  }, [fetchSignals]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    await fetchSignals({ ...activeFiltersRef.current, limit: 50, cursor: nextCursor }, true);
  }, [nextCursor, loadingMore, fetchSignals]);

  // GitHub #683: explicit "show muted signals" affordance — re-fetches the
  // current filter set with respectMutes:false. Session-local only (not
  // persisted to activeFiltersRef), so the next normal refresh/poll goes
  // back to respecting mutes.
  const showMutedSignals = useCallback(async () => {
    await fetchSignals({ ...activeFiltersRef.current, limit: 50, respectMutes: false });
  }, [fetchSignals]);

  const markRead = useCallback(async (signalId: string) => {
    const now = new Date().toISOString();
    // Optimistic update for both lists
    setSignals(prev => prev.map(s => s.id === signalId ? { ...s, read: true, readAt: now } : s));
    setPanelSignals(prev => prev.map(s => s.id === signalId ? { ...s, read: true, readAt: now } : s));
    setPanelActedOnSignals(prev => prev.map(s => s.id === signalId ? { ...s, read: true, readAt: now } : s));
    setUnreadCount(c => Math.max(0, c - 1));

    try {
      await apiFetch(`/api/v1/signals/${signalId}/read`, { method: 'POST' });
    } catch (err: any) {
      console.error('[SignalFeed] markRead error:', err.message);
      // Revert optimistic update
      setSignals(prev => prev.map(s => s.id === signalId ? { ...s, read: false, readAt: undefined } : s));
      setPanelSignals(prev => prev.map(s => s.id === signalId ? { ...s, read: false, readAt: undefined } : s));
      setPanelActedOnSignals(prev => prev.map(s => s.id === signalId ? { ...s, read: false, readAt: undefined } : s));
      setUnreadCount(c => c + 1);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    const previousSignals = signals;
    const previousPanelSignals = panelSignals;
    const previousPanelActedOnSignals = panelActedOnSignals;
    const previousCount = unreadCount;
    const now = new Date().toISOString();

    // Optimistic update for both lists
    setSignals(prev => prev.map(s => ({ ...s, read: true, readAt: now })));
    setPanelSignals(prev => prev.map(s => ({ ...s, read: true, readAt: now })));
    setPanelActedOnSignals(prev => prev.map(s => ({ ...s, read: true, readAt: now })));
    setUnreadCount(0);
    setByPriority(defaultByPriority);

    try {
      await apiFetch('/api/v1/signals/read-all', { method: 'POST' });
    } catch (err: any) {
      console.error('[SignalFeed] markAllRead error:', err.message);
      setSignals(previousSignals);
      setPanelSignals(previousPanelSignals);
      setPanelActedOnSignals(previousPanelActedOnSignals);
      setUnreadCount(previousCount);
    }
  }, [signals, panelSignals, panelActedOnSignals, unreadCount]);

  // Patches recommendedAction.status and, since the backend's default
  // (unresolved) query now excludes applied/declined signals, moves the
  // signal out of the Active-tab lists (signals, panelSignals) and into the
  // Acted On preview (panelActedOnSignals) so the UI doesn't keep showing a
  // resolved item as if it still needed attention. Only ever called for a
  // 'pending' signal (the button that triggers it is gated on that status),
  // so it's always moving out of the Active tab, never the reverse.
  const resolveRecommendedAction = useCallback(
    (signalId: string, patch: Partial<Signal['recommendedAction']>) => {
      let resolved: Signal | undefined;
      setSignals(prev => {
        const idx = prev.findIndex(s => s.id === signalId);
        if (idx === -1) return prev;
        resolved = { ...prev[idx], recommendedAction: prev[idx].recommendedAction ? { ...prev[idx].recommendedAction, ...patch } : prev[idx].recommendedAction };
        return prev.filter(s => s.id !== signalId);
      });
      setPanelSignals(prev => {
        const idx = prev.findIndex(s => s.id === signalId);
        if (idx === -1) return prev;
        resolved = resolved ?? { ...prev[idx], recommendedAction: prev[idx].recommendedAction ? { ...prev[idx].recommendedAction, ...patch } : prev[idx].recommendedAction };
        return prev.filter(s => s.id !== signalId);
      });
      if (resolved) {
        const patched = resolved;
        setPanelActedOnSignals(prev => [patched, ...prev.filter(s => s.id !== signalId)].slice(0, 10));
      }
    },
    [],
  );

  const addRecommendedActionToQueue = useCallback(async (signalId: string, overrides?: Partial<CreateActionInput>) => {
    const res = await apiFetch<SignalActionResponse>(`/api/v1/signals/${signalId}/action`, {
      method: 'POST',
      body: overrides ? JSON.stringify({ overrides }) : undefined,
    });
    resolveRecommendedAction(signalId, { status: 'applied' });
    // KPI cards read a separate SWR cache and won't pick up this signal-driven
    // task creation on their own poll cycle — refresh it immediately. The
    // Action Queue itself is a React Query cache (useActionQueue.ts) that
    // this signal-driven creation bypasses entirely, so it must be
    // invalidated here too or the new task won't appear until something else
    // triggers a refetch (GitHub #620).
    refreshPortfolioSummary();
    queryClient.invalidateQueries({ queryKey: actionQueueQueryKey });
    return res.action_id;
  }, [resolveRecommendedAction, queryClient]);

  const dismissRecommendedAction = useCallback(async (signalId: string, reason?: DismissReason) => {
    await apiFetch(`/api/v1/signals/${signalId}/dismiss-action`, {
      method: 'POST',
      body: reason ? JSON.stringify({ dismiss_reason: reason }) : undefined,
    });
    resolveRecommendedAction(signalId, { status: 'declined' });
    refreshPortfolioSummary();
    queryClient.invalidateQueries({ queryKey: actionQueueQueryKey });
  }, [resolveRecommendedAction, queryClient]);

  // Moves a signal from the Active lists into the Acted On preview without
  // touching recommendedAction — used when a signal is manually linked to a
  // Risk/Commitment/CSM Note/Feature Request/Opportunity via the "Add to:"
  // menu (SignalDetailModal), which the backend's active-linked-signal
  // suppression (GitHub #623) now also treats as resolved. Local mirror of
  // that server-side suppression so the card disappears immediately instead
  // of waiting for the next poll/refetch.
  const markSignalActedOn = useCallback((signalId: string) => {
    let resolved: Signal | undefined;
    setSignals(prev => {
      const idx = prev.findIndex(s => s.id === signalId);
      if (idx === -1) return prev;
      resolved = prev[idx];
      return prev.filter(s => s.id !== signalId);
    });
    setPanelSignals(prev => {
      const idx = prev.findIndex(s => s.id === signalId);
      if (idx === -1) return prev;
      resolved = resolved ?? prev[idx];
      return prev.filter(s => s.id !== signalId);
    });
    if (resolved) {
      const patched = resolved;
      setPanelActedOnSignals(prev => [patched, ...prev.filter(s => s.id !== signalId)].slice(0, 10));
    }
    // KPI cards read a separate SWR cache — refresh it immediately instead of
    // waiting for the next poll.
    refreshPortfolioSummary();
  }, []);

  // ---- Urgent signal polling ----
  const pollUrgent = useCallback(async () => {
    if (!featureEnabled) return;
    try {
      const since = lastUrgentPollRef.current;
      lastUrgentPollRef.current = new Date().toISOString();

      const res = await apiFetch<SignalFeedResponse>(
        buildSignalsUrl({ priority: 'urgent', since, read: false, limit: 10 }),
      );

      for (const signal of normalizeSignals(res.signals)) {
        toast(signal.title, {
          description: signal.accountName,
          action: {
            label: 'View',
            onClick: () => navigateToAccount(`${signal.accountId}/customer`),
          },
          duration: 8000,
        });
      }

      // Refresh unread count + panel signals after urgent poll
      if (res.signals.length > 0) {
        const countRes = await apiFetch<UnreadCountResponse>('/api/v1/signals/unread-count');
        setUnreadCount(countRes.unreadCount ?? 0);
        setByPriority(countRes.byPriority ?? defaultByPriority);
        // Refresh panel signals to pick up newly arrived signals, keeping the
        // currently selected My/All Customers scope (panelViewAll) — an
        // unscoped refetch here would silently snap the panel back to "my
        // accounts" while an admin is viewing "All Customers".
        const panelRes = await apiFetch<SignalFeedResponse>(buildSignalsUrl({ limit: 10, viewAll: panelViewAll }));
        setPanelSignals(normalizeSignals(panelRes.signals));
      }
    } catch (err: any) {
      console.warn('[SignalFeed] urgentPoll error:', err.message);
    }
  }, [featureEnabled, panelViewAll, router]);

  // Start/stop urgent polling + Page Visibility
  useEffect(() => {
    if (!featureEnabled || !isAuthenticated) return;

    const startPolling = () => {
      if (urgentTimerRef.current) return;
      urgentTimerRef.current = setInterval(pollUrgent, URGENT_POLL_INTERVAL);
    };

    const stopPolling = () => {
      if (urgentTimerRef.current) {
        clearInterval(urgentTimerRef.current);
        urgentTimerRef.current = null;
      }
    };

    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        // Immediate poll on tab focus, then resume interval
        pollUrgent();
        startPolling();
      }
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [featureEnabled, isAuthenticated, pollUrgent]);

  // ---- Silent periodic refresh of the general filtered list ----
  // Unlike pollUrgent (priority: 'urgent' only), this re-fetches whatever
  // filters are currently active so daily/hourly-cadence signal categories
  // become visible without a manual refresh or filter change.
  const pollList = useCallback(async () => {
    if (!featureEnabled) return;
    await fetchSignals({ limit: 50, ...activeFiltersRef.current }, false, true);
  }, [featureEnabled, fetchSignals]);

  useEffect(() => {
    if (!featureEnabled || !isAuthenticated) return;

    const startPolling = () => {
      if (listTimerRef.current) return;
      listTimerRef.current = setInterval(pollList, LIST_POLL_INTERVAL);
    };

    const stopPolling = () => {
      if (listTimerRef.current) {
        clearInterval(listTimerRef.current);
        listTimerRef.current = null;
      }
    };

    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        startPolling();
      }
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [featureEnabled, isAuthenticated, pollList]);

  // ---- Real-time delivery (SSE bridge, Phase 2) ----
  // Polling above stays as the fallback for browsers/proxies that drop
  // long-lived SSE connections — this stream is additive, not a replacement.
  useEffect(() => {
    if (!featureEnabled || !isAuthenticated) return;
    if (process.env.NEXT_PUBLIC_NOTIFICATIONS_SSE_ENABLED === 'false') return;
    if (typeof window === 'undefined' || typeof EventSource === 'undefined') return;

    const currentUserId = session?.user?.id;
    let closed = false;
    let es: EventSource | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let retryDelay = 2000;
    const MAX_RETRY_DELAY_MS = 30000;

    const connect = () => {
      if (closed) return;
      es = new EventSource('/api/notifications-proxy/stream');

      es.onopen = () => {
        retryDelay = 2000;
      };

      es.addEventListener('signal', (evt) => {
        try {
          const raw = JSON.parse((evt as MessageEvent).data);
          // Org-wide signals (target_user_id null) are visible to everyone;
          // user-scoped system notifications only to their recipient —
          // mirrors the visibility rule urgent_signal_service.list_signals applies.
          if (raw.target_user_id && raw.target_user_id !== currentUserId) return;

          const signal = normalizeSignals([raw])[0];
          if (!signal?.id) return;

          // Task-derived detector signals (task overdue, due tomorrow, playbook
          // deadlines) are never part of the feed (GitHub #1054): the backend
          // hides them from every list, so a live event must not add them
          // either. No toast — the assignee's bell entry is their notice.
          if (TASK_DERIVED_SIGNAL_CATEGORIES.has(raw.category)) return;

          // System notifications (task assigned, status changed, ...) belong
          // to the Command Hub bell, not the Signal Feed (GitHub #1054): keep
          // the real-time toast, ask the bell to refetch, and skip the feed
          // lists and unread badge. The bell row is written server-side just
          // after this event publishes, hence the short delay.
          if (raw.notification_source === 'system') {
            toast(signal.title, {
              description: signal.description || undefined,
              duration: 8000,
            });
            window.setTimeout(() => {
              window.dispatchEvent(new CustomEvent(BELL_REFRESH_EVENT));
            }, 1500);
            return;
          }

          // A recurring signal is escalated in place server-side rather than
          // reinserted (GitHub #623) — event_type distinguishes a genuinely
          // new signal from an update to one already loaded, since the old
          // id-exists-so-skip check silently dropped escalation updates.
          // Membership is checked inside the functional updater (not against
          // outer `signals` state) to avoid a stale-closure read.
          if (raw.event_type === 'signal_updated') {
            const patchOrPrepend = (list: Signal[]) => {
              const idx = list.findIndex(s => s.id === signal.id);
              if (idx === -1) return [signal, ...list];
              const next = [...list];
              next[idx] = { ...next[idx], ...signal };
              return next;
            };
            setSignals(patchOrPrepend);
            setPanelSignals(prev => patchOrPrepend(prev).slice(0, 10));
            // Priority/unread counts self-correct on the next unread-count
            // poll rather than being adjusted here — the true source of
            // truth is per-user read-receipt state, which this event alone
            // can't reflect (the server clears receipts on escalation).
            return;
          }

          setSignals(prev => (prev.some(s => s.id === signal.id) ? prev : [signal, ...prev]));
          setPanelSignals(prev =>
            prev.some(s => s.id === signal.id) ? prev : [signal, ...prev].slice(0, 10)
          );
          setUnreadCount(c => c + 1);
          setByPriority(prev => ({
            ...prev,
            [signal.priority]: (prev[signal.priority] ?? 0) + 1,
          }));

          toast(signal.title, {
            description: signal.accountName || undefined,
            action: signal.accountId
              ? { label: 'View', onClick: () => navigateToAccount(`${signal.accountId}/customer`) }
              : undefined,
            duration: 8000,
          });
        } catch (err) {
          console.warn('[SignalFeed] SSE signal parse error:', err);
        }
      });

      es.addEventListener('signal_suggestions', (evt) => {
        // Signal Engine: patch suggestionCount onto the matching signal by
        // signal_id. Delivered as a distinct event type (not folded into
        // 'signal' above) since suggestions are computed slightly after the
        // signal write in a later pipeline step — a separate event avoids
        // guessing whether an absent count means "zero" or "not yet computed".
        try {
          const raw = JSON.parse((evt as MessageEvent).data);
          const signalId = raw.signal_id;
          const count = raw.suggestion_count ?? 0;
          if (!signalId) return;

          const patch = (list: Signal[]) =>
            list.map(s => (s.id === signalId ? { ...s, suggestionCount: count } : s));
          setSignals(patch);
          setPanelSignals(patch);
        } catch (err) {
          console.warn('[SignalFeed] SSE signal_suggestions parse error:', err);
        }
      });

      es.addEventListener('discovery_event', (evt) => {
        // Discovery Workspace UI (not the notification bell) consumes this.
        // Broadcast as a DOM event so those components can subscribe without
        // this context needing to know about discovery-specific payloads.
        window.dispatchEvent(
          new CustomEvent('discovery-event', { detail: (evt as MessageEvent).data })
        );
      });

      es.onerror = () => {
        // A CLOSED readyState means the backend ended the connection (e.g. a
        // 503 while the feature flag is off) — native EventSource does not
        // auto-retry from CLOSED, so back off and reopen ourselves. A
        // CONNECTING readyState means EventSource is already retrying on its
        // own; no action needed.
        if (es?.readyState === EventSource.CLOSED && !closed) {
          es.close();
          retryTimer = setTimeout(connect, retryDelay);
          retryDelay = Math.min(retryDelay * 2, MAX_RETRY_DELAY_MS);
        }
      };
    };

    connect();

    return () => {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      es?.close();
    };
  }, [featureEnabled, isAuthenticated, session?.user?.id, router]);

  return (
    <SignalFeedContext.Provider
      value={{
        signals,
        panelSignals,
        panelActedOnSignals,
        panelTotalCount,
        panelActedOnTotalCount,
        totalCount,
        mutedCount,
        showMutedSignals,
        unreadCount,
        byPriority,
        featureEnabled,
        loading,
        loadingMore,
        fetchError,
        nextCursor,
        markRead,
        markAllRead,
        addRecommendedActionToQueue,
        dismissRecommendedAction,
        markSignalActedOn,
        refreshSignals,
        loadMore,
        setPanelViewAll,
      }}
    >
      {children}
    </SignalFeedContext.Provider>
  );
}

// ==================== Hook ====================

export function useSignalFeed() {
  return useContext(SignalFeedContext);
}

// ==================== Utilities ====================

function buildSignalsUrl(filters: SignalFilterOptions): string {
  const params = new URLSearchParams();
  if (filters.priority) params.append('priority', filters.priority);
  if (filters.accountId) params.append('account_id', filters.accountId);
  if (filters.connector) params.append('connector', filters.connector);
  if (filters.category && filters.category.length > 0) params.append('category', filters.category.join(','));
  if (filters.since) params.append('since', filters.since);
  if (filters.read !== undefined) params.append('read', String(filters.read));
  if (filters.limit !== undefined) params.append('limit', String(filters.limit));
  if (filters.cursor) params.append('cursor', filters.cursor);
  if (filters.viewAll) params.append('viewAll', 'true');
  if (filters.actedOn) params.append('actedOn', 'true');
  if (filters.respectMutes === false) params.append('respect_mutes', 'false');
  const qs = params.toString();
  return `/api/v1/signals${qs ? `?${qs}` : ''}`;
}

/**
 * Derive a SignalCategory from the backend signal_type string.
 * Backend uses formats like "gong:sentiment", "expansion:opportunity", "churn:risk".
 * The second segment (or the whole string) is matched to a known category.
 */
function categoryFromSignalType(signalType?: string): SignalCategory {
  if (!signalType) return 'other';
  const lower = signalType.toLowerCase();
  if (lower.includes('expansion')) return 'expansion';
  if (lower.includes('churn')) return 'churn_risk';
  if (lower.includes('escalat')) return 'escalation';
  if (lower.includes('sentiment')) return 'sentiment';
  if (lower.includes('contact')) return 'contact_changes';
  if (lower.includes('health')) return 'health_scores';
  if (lower.includes('engagement')) return 'engagement_decline';
  if (lower.includes('renewal')) return 'renewal';
  if (lower.includes('support')) return 'tickets';
  if (lower.includes('usage') || lower.includes('product')) return 'usage';
  return 'other';
}

/** Map backend evidence items (snake_case) to SignalEvidence, dropping any
 * item without a source and summary. Also accepts the older
 * `{source, detail, timestamp}` shape. */
function normalizeEvidence(raw: unknown): SignalEvidence[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((e: any) => {
    if (!e || typeof e !== 'object') return [];
    const summary = e.summary ?? e.detail;
    if (!e.source || !summary) return [];
    return [{
      source: e.source,
      summary,
      recordType: e.record_type ?? e.recordType ?? undefined,
      occurredAt: e.occurred_at ?? e.occurredAt ?? e.timestamp ?? undefined,
      snippet: e.snippet ?? undefined,
      url: e.url ?? undefined,
    }];
  });
}

function normalizeBusinessContext(raw: any): SignalBusinessContext | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const trend = raw.health_trend ?? raw.healthTrend;
  return {
    arr: raw.arr ?? null,
    renewalDate: raw.renewal_date ?? raw.renewalDate ?? null,
    daysToRenewal: raw.days_to_renewal ?? raw.daysToRenewal ?? null,
    healthScore: raw.health_score ?? raw.healthScore ?? null,
    healthTrend: trend ? { direction: trend.direction, delta: trend.delta ?? null, window: trend.window ?? null } : null,
    segment: raw.segment ?? null,
  };
}

function normalizePortfolioRollup(raw: any): SignalPortfolioRollup | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const accountCount = raw.account_count ?? raw.accountCount;
  if (typeof accountCount !== 'number') return undefined;
  return {
    category: raw.category ?? '',
    label: raw.label ?? '',
    accountCount,
    totalArr: raw.total_arr ?? raw.totalArr ?? null,
    windowDays: raw.window_days ?? raw.windowDays ?? 30,
  };
}

/** Normalize snake_case keys from backend to camelCase Signal objects.
 * Exported so single-signal fetches (e.g. hooks/useSignalDetail.ts) can
 * reuse the same mapping instead of duplicating it. */
export function normalizeSignals(raw: any[]): Signal[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((s: any) => ({
    // Backend may use signal_id, id, or _id
    id: s.id ?? s._id ?? s.signal_id ?? '',
    title: s.title ?? '',
    description: s.description ?? '',
    priority: s.priority ?? 'low',
    // Backend may use category directly or encode it in signal_type
    category: s.category ?? categoryFromSignalType(s.signal_type),
    // Backend may use connector, connector_type, or connectorType
    connectorType: s.connector_type ?? s.connectorType ?? s.connector ?? 'system',
    accountId: s.account_id ?? s.accountId ?? '',
    accountName: s.account_name ?? s.accountName ?? '',
    read: s.read ?? false,
    readAt: s.read_at ?? s.readAt,
    createdAt: s.created_at ?? s.createdAt ?? s.created_time ?? new Date().toISOString(),
    updatedAt: s.updated_at ?? s.updatedAt,
    evidence: normalizeEvidence(s.evidence),
    confidence: typeof s.confidence === 'number' ? s.confidence : undefined,
    businessContext: normalizeBusinessContext(s.business_context ?? s.businessContext),
    portfolioRollup: normalizePortfolioRollup(s.portfolio_rollup ?? s.portfolioRollup),
    metadata: s.metadata ?? s.details,
    recommendedAction: s.recommended_action ?? s.recommendedAction ?? undefined,
    volumeCapped: s.volume_capped ?? s.volumeCapped ?? false,
    correlatedSignalIds: s.correlated_signal_ids ?? s.correlatedSignalIds ?? [],
  }));
}
